import React from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const RequirementsTable = ({
  requirements = [],
  loading = false,
  onRequirementClick,
  onShowMatching,
  selectedRequirements = [],
  onSelectionChange
}) => {
  
  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(requirements.map(req => req.id));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectItem = (requirementId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedRequirements, requirementId]);
    } else {
      onSelectionChange?.(selectedRequirements.filter(id => id !== requirementId));
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      open: 'bg-green-100 text-green-800 border-green-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      partially_filled: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed: 'bg-gray-100 text-gray-800 border-gray-200',
      on_hold: 'bg-orange-100 text-orange-800 border-orange-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status] || statusStyles.open}`}>
        {status?.replace('_', ' ') || 'Open'}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityStyles = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };

    const priorityIcons = {
      low: '🔵',
      medium: '🟡',
      high: '🟠',
      urgent: '🔴'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${priorityStyles[priority] || priorityStyles.medium}`}>
        <span className="mr-1">{priorityIcons[priority] || '🔵'}</span>
        {priority || 'Medium'}
      </span>
    );
  };

  const getMatchingScore = (requirement) => {
    const matches = requirement.candidate_matches || [];
    if (matches.length === 0) return 0;
    
    const avgScore = matches.reduce((sum, match) => sum + (match.compatibility_score || 0), 0) / matches.length;
    return Math.round(avgScore);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <span className="text-red-600 font-medium">Overdue</span>;
    } else if (diffDays === 0) {
      return <span className="text-orange-600 font-medium">Today</span>;
    } else if (diffDays <= 3) {
      return <span className="text-orange-600 font-medium">{diffDays} days</span>;
    } else {
      return <span className="text-muted-foreground">{diffDays} days</span>;
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border">
        <div className="p-6 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-muted-foreground mt-2">Loading requirements...</p>
        </div>
      </div>
    );
  }

  if (requirements.length === 0) {
    return (
      <div className="bg-card rounded-lg border">
        <div className="p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
            📋
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No Requirements Found</h3>
          <p className="text-muted-foreground mb-4">
            There are no client requirements matching your current filters.
          </p>
          <Button iconName="Plus" iconPosition="left">
            Create New Requirement
          </Button>
        </div>
      </div>
    );
  }

  const isAllSelected = requirements.length > 0 && selectedRequirements.length === requirements.length;
  const isPartiallySelected = selectedRequirements.length > 0 && selectedRequirements.length < requirements.length;

  return (
    <div className="bg-card rounded-lg border overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b">
            <tr>
              <th className="w-12 p-4 text-left">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isPartiallySelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="text-left p-4 font-medium text-foreground">Client & Position</th>
              <th className="text-left p-4 font-medium text-foreground">Requirements</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="text-left p-4 font-medium text-foreground">Priority</th>
              <th className="text-left p-4 font-medium text-foreground">Deadline</th>
              <th className="text-left p-4 font-medium text-foreground">Linked Leads</th>
              <th className="text-left p-4 font-medium text-foreground">Matches</th>
              <th className="text-left p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {requirements.map((requirement) => (
              <tr key={requirement.id} className="hover:bg-muted/20 transition-colors">
                <td className="p-4">
                  <Checkbox
                    checked={selectedRequirements.includes(requirement.id)}
                    onChange={(checked) => handleSelectItem(requirement.id, checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="space-y-1">
                    <button
                      onClick={() => onRequirementClick?.(requirement)}
                      className="font-medium text-foreground hover:text-primary text-left"
                    >
                      {requirement.clients?.company_name || 'Unknown Client'}
                    </button>
                    <p className="text-sm text-muted-foreground">
                      {requirement.position_title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      📍 {requirement.location}
                    </p>
                  </div>
                </td>
                <td className="p-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {requirement.quantity_needed} position{requirement.quantity_needed !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {requirement.experience_level}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(requirement.required_skills || []).slice(0, 3).map((skill, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                          {skill}
                        </span>
                      ))}
                      {(requirement.required_skills || []).length > 3 && (
                        <span className="text-xs text-muted-foreground">
                          +{(requirement.required_skills || []).length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  {getStatusBadge(requirement.status)}
                </td>
                <td className="p-4">
                  {getPriorityBadge(requirement.priority)}
                </td>
                <td className="p-4 text-sm">
                  {formatDate(requirement.deadline)}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    {(requirement.linked_leads_count || 0) > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        📤 {requirement.linked_leads_count} sent
                      </span>
                    )}
                    {(requirement.accepted_leads_count || 0) > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                        ✅ {requirement.accepted_leads_count} accepted
                      </span>
                    )}
                    {(requirement.linked_leads_count || 0) === 0 && (
                      <span className="text-xs text-muted-foreground">No leads sent</span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium">
                        {(requirement.candidate_matches || []).length}
                      </span>
                      <span className="text-xs text-muted-foreground">matches</span>
                    </div>
                    {(requirement.candidate_matches || []).length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">
                          {getMatchingScore(requirement)}% avg
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Users"
                      onClick={() => onShowMatching?.(requirement)}
                    >
                      Match
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onRequirementClick?.(requirement)}
                    >
                      View
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y">
        {requirements.map((requirement) => (
          <div key={requirement.id} className="p-4 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedRequirements.includes(requirement.id)}
                  onChange={(checked) => handleSelectItem(requirement.id, checked)}
                />
                <div>
                  <button
                    onClick={() => onRequirementClick?.(requirement)}
                    className="font-medium text-foreground hover:text-primary text-left"
                  >
                    {requirement.clients?.company_name || 'Unknown Client'}
                  </button>
                  <p className="text-sm text-muted-foreground">
                    {requirement.position_title}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getPriorityBadge(requirement.priority)}
                {getStatusBadge(requirement.status)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Positions:</span>
                <span className="ml-2 font-medium">{requirement.quantity_needed}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Experience:</span>
                <span className="ml-2">{requirement.experience_level}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Location:</span>
                <span className="ml-2">📍 {requirement.location}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Deadline:</span>
                <span className="ml-2">{formatDate(requirement.deadline)}</span>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Linked Leads:</span>
                <div className="flex items-center gap-2 mt-1">
                  {(requirement.linked_leads_count || 0) > 0 ? (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        📤 {requirement.linked_leads_count} sent
                      </span>
                      {(requirement.accepted_leads_count || 0) > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          ✅ {requirement.accepted_leads_count} accepted
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">No leads sent</span>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Required Skills:</p>
              <div className="flex flex-wrap gap-1">
                {(requirement.required_skills || []).slice(0, 4).map((skill, idx) => (
                  <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                    {skill}
                  </span>
                ))}
                {(requirement.required_skills || []).length > 4 && (
                  <span className="text-xs text-muted-foreground">
                    +{(requirement.required_skills || []).length - 4} more
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{(requirement.candidate_matches || []).length}</span>
                <span className="text-muted-foreground">matches</span>
                {(requirement.candidate_matches || []).length > 0 && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">{getMatchingScore(requirement)}% avg</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Users"
                  onClick={() => onShowMatching?.(requirement)}
                >
                  Match
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  onClick={() => onRequirementClick?.(requirement)}
                >
                  View
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RequirementsTable;